import { transactionIdSetter } from "helpers/transactionIdFilter";
import React, { useState, useEffect } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { FilterItemTypes } from "configs/types";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { useAppSelector } from "store/index";

import Filter from "components/global/Filter/Filter";

import gridIcon from "assets/icons/grid-icon.svg";

interface Props {
  handleUpdateProviders: (
    e: { value: string | number; label: string }[],
  ) => void;
  selectedProviders: { value: string | number; label: string }[];
  handleUpdateCurrencies: (
    e: { value: string | number; label: string }[],
  ) => void;
  selectedCurrencies: { value: string | number; label: string }[];
  handleUpdateStatuses: (
    e: { value: string | number; label: string }[],
  ) => void;
  selectedStatuses: { value: string | number; label: string }[];
  handleUpdateMethods: (e: { value: string | number; label: string }[]) => void;
  selectedMethods: { value: string | number; label: string }[];
  handleUpdateTypes: (e: { value: string | number; label: string }[]) => void;
  selectedTypes: { value: string | number; label: string }[];
  depositId: string;
  setDepositId: (value: string) => void;
  userId: string;
  setUserId: (value: string) => void;
  externalRef: string;
  setExternalRef: (value: string) => void;
  createdFrom: string;
  createdTo: string;
  setCreatedRange: (from: string, to: string) => void;
  completedFrom: string;
  completedTo: string;
  setCompletedRange: (from: string, to: string) => void;
  handleApply: () => void;
}
const DepositList: React.FC<Props> = ({
  handleUpdateProviders,
  selectedProviders,
  handleUpdateCurrencies,
  selectedCurrencies,
  handleUpdateStatuses,
  selectedStatuses,
  handleUpdateMethods,
  selectedMethods,
  handleUpdateTypes,
  selectedTypes,
  depositId,
  setDepositId,
  userId,
  setUserId,
  externalRef,
  setExternalRef,
  createdFrom,
  createdTo,
  setCreatedRange,
  completedFrom,
  completedTo,
  setCompletedRange,
  handleApply,
}) => {
  const { t } = useTranslation();

  const currenciesList = useAppSelector((store) => store.settings.currencies);
  const statusesList = useAppSelector(
    (store) => store.paymentsDeposit.statusesList,
  );

  const [providers, setProviders] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [methods, setMethods] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [currencies, setCurrencies] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [statuses, setStatuses] = useState<{ value: string; label: string }[]>(
    [],
  );
  const depositTypes = [
    { value: "deposit", label: "Regular" },
    { value: "manual_deposit", label: "Manual" },
  ];

  const getProviders = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.paymentProvidersList}?limit=1000&offset=0`,
      );
      const array =
        result.data.items?.map((el: any) => {
          return {
            value: el.id,
            label: el.title,
          };
        }) ?? [];
      setProviders(array);
    } catch (error) {
      console.log(error, "error");
    }
  };
  const getMethods = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.paymentMethodList}?limit=1000&offset=0`,
      );
      const array =
        result.data.items?.map((el: any) => {
          return {
            value: el.id,
            label: el.title,
          };
        }) ?? [];
      setMethods(array);
    } catch (error) {
      console.log(error, "error");
    }
  };
  useEffect(() => {
    getProviders();
    getMethods();
  }, []);
  useEffect(() => {
    const result =
      currenciesList?.map((el) => {
        return {
          value: el,
          label: el,
        };
      }) ?? [];
    setCurrencies(result);
  }, [currenciesList]);
  useEffect(() => {
    const result = statusesList?.map((el) => el) ?? [];
    setStatuses(result);
  }, [statusesList]);

  const handleApplyProviders = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateProviders(isDelete ? e : [...selectedProviders, ...e]);
  };
  const handleApplyMethods = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateMethods(isDelete ? e : [...selectedMethods, ...e]);
  };
  const handleApplyCurrencies = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateCurrencies(isDelete ? e : [...selectedCurrencies, ...e]);
  };
  const handleApplyStatuses = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateStatuses(isDelete ? e : [...selectedStatuses, ...e]);
  };
  const handleApplyTypes = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateTypes(isDelete ? e : [...selectedTypes, ...e]);
  };

  const handleClearFilters = () => {
    handleUpdateCurrencies([]);
    handleUpdateStatuses([]);
    handleUpdateProviders([]);
    handleUpdateMethods([]);
    handleUpdateTypes([]);
    setDepositId("");
    setUserId("");
    setExternalRef("");
    setCreatedRange("", "");
    setCompletedRange("", "");
    handleApply();
  };

  const handleApplyFilters = () => {
    handleApply();
  };

  return (
    <Filter
      handleClear={handleClearFilters}
      handleApply={handleApplyFilters}
      filterItems={[
        {
          type: FilterItemTypes.input,
          title: "Deposit ID",
          boxTitle: "Deposit ID",
          icon: gridIcon,
          inputValue: depositId,
          setInputValue: transactionIdSetter(setDepositId),
        },
        {
          type: FilterItemTypes.input,
          title: "User ID",
          boxTitle: "User ID",
          icon: gridIcon,
          inputValue: userId,
          setInputValue: transactionIdSetter(setUserId, true),
        },
        {
          type: FilterItemTypes.input,
          title: "External ID",
          boxTitle: "External ID",
          icon: gridIcon,
          inputValue: externalRef,
          setInputValue: setExternalRef,
        },
        {
          type: FilterItemTypes.multiselect,
          title: "Type",
          boxTitle: "Type",
          icon: gridIcon,
          itemsArray: depositTypes,
          activeItems: selectedTypes,
          handleApply: handleApplyTypes,
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("providers"),
          boxTitle: t("filterProvider"),
          icon: gridIcon,
          itemsArray: providers,
          activeItems: selectedProviders,
          handleApply: handleApplyProviders,
          disabled: providers.length === 0,
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("methods"),
          boxTitle: t("filterMethods"),
          icon: gridIcon,
          itemsArray: methods,
          activeItems: selectedMethods,
          handleApply: handleApplyMethods,
          disabled: methods.length === 0,
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("currencies"),
          boxTitle: t("filterCurrencies"),
          icon: gridIcon,
          itemsArray: currencies,
          activeItems: selectedCurrencies,
          handleApply: handleApplyCurrencies,
          disabled: currencies.length === 0,
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("statuses"),
          boxTitle: t("filterStatuses"),
          icon: gridIcon,
          itemsArray: statuses,
          activeItems: selectedStatuses,
          handleApply: handleApplyStatuses,
          disabled: statuses.length === 0,
        },
        {
          type: FilterItemTypes.datePicker,
          title: "Created At",
          boxTitle: "Created At",
          icon: gridIcon,
          firstDatePlaceholder: "Created At From",
          secondDatePlaceholder: "Created At To",
          firstDateInitValue: createdFrom,
          secondDateInitValue: createdTo,
          handleApplyDate: (from, to) => setCreatedRange(from, to ?? ""),
          activeItems: [
            ...(createdFrom
              ? [{ value: "from", label: `From: ${createdFrom}` }]
              : []),
            ...(createdTo ? [{ value: "to", label: `To: ${createdTo}` }] : []),
          ],
          onDeleteItem: (value) =>
            setCreatedRange(
              value === "from" ? "" : createdFrom,
              value === "to" ? "" : createdTo,
            ),
        },
        {
          type: FilterItemTypes.datePicker,
          title: "Completed At",
          boxTitle: "Completed At",
          icon: gridIcon,
          firstDatePlaceholder: "Completed At From",
          secondDatePlaceholder: "Completed At To",
          firstDateInitValue: completedFrom,
          secondDateInitValue: completedTo,
          handleApplyDate: (from, to) => setCompletedRange(from, to ?? ""),
          activeItems: [
            ...(completedFrom
              ? [{ value: "from", label: `From: ${completedFrom}` }]
              : []),
            ...(completedTo
              ? [{ value: "to", label: `To: ${completedTo}` }]
              : []),
          ],
          onDeleteItem: (value) =>
            setCompletedRange(
              value === "from" ? "" : completedFrom,
              value === "to" ? "" : completedTo,
            ),
        },
      ]}
    />
  );
};

export const toIsoDateTime = (value: string) =>
  moment(value, "DD.MM.YYYY HH:mm", true).toISOString();

export default DepositList;
